import { KeyValuePair } from "@app/types/Page";
import AdIMUPlaceholder from "@components/Advertisement/components/AdIMUPlaceholder";
import AdLeaderboardPlaceholder from "@components/Advertisement/components/AdLeaderboardPlaceholder";
import AdMASPlaceholder from "@components/Advertisement/components/AdMASPlaceholde";
import { GoogleAdsSlotFactory } from "@util/helpers";

import {
  adPositions,
  adSizeMappings,
  adSlotSizes,
  adWeights,
} from "./constants";
import { AdUnitNames } from "./types";

/**
 * Helper function to get ad unit data.
 *
 * @param adNames The list of ad names.
 * @param path The current path.
 * @param counter The counter.
 * @returns The ad unit data array.
 */
export const getAdUnitData = (
  adNames: AdUnitNames[],
  path: string,
  slotTargets: KeyValuePair<string, string>[] = []
) => {
  const modifiedAdNames = movePrestitialInFront(adNames);

  return modifiedAdNames.map((adName) => {
    return {
      id: generateAdId(adName),
      type: adName,
      pattern: GoogleAdsSlotFactory.prefix + "/" + adName + path,
      slotSize: adSlotSizes[adName] || {},
      sizeMapping: adSizeMappings[adName] || [],
      position: adPositions[adName] || "1",
      weight: adWeights[adName] || "1",
      slotTargets: slotTargets,
    };
  });
};

/**
 * Helper function to move prestitial in front if it exists.
 * @param adNames The list of ad names.
 * @returns {AdUnitNames[]} The modified list of ad names.
 */
const movePrestitialInFront = (adNames: AdUnitNames[]): AdUnitNames[] => {
  if (adNames.includes("prestitial")) {
    return [
      "prestitial",
      ...adNames.filter((adunit) => adunit !== "prestitial"),
    ];
  }

  return adNames;
};

/**
 * Helper function to generate ad id.
 * @param adType The ad type.
 * @param counter The counter.
 * @returns {string} The ad id.
 */
export const generateAdId = (
  adType: AdUnitNames,
  counter: number = 0
): string => {
  if (counter === 0) return `dfp-ad-${adType}`;

  return `dfp-ad-${adType}-${counter}`;
};

/**
 * Helper function to get the placeholder component for the ad type.
 * @param adtype The ad type name.
 * @returns {(() => React.ReactElement) | undefined}
 */
export const AdPlaceholderFactory = (
  adtype: AdUnitNames
): (() => React.ReactElement) | undefined => {
  switch (adtype) {
    case "lb1":
    case "lb2":
      return AdLeaderboardPlaceholder;

    case "imu1":
    case "imu2":
    case "imu3":
      return AdIMUPlaceholder;

    case "midarticlespecial":
      return AdMASPlaceholder;
  }
};

/**
 * Helper function to format a text that is suited for Ads Team's requirements.
 * Examples of keyword text shown on helpers.test.tsx
 * @param {string} text The text that needs to be formatted.
 * @returns {string}
 */
export const getFormattedTextForAds = (text: string): string => {
  return text
    .replaceAll(/&amp;|&/g, "and")
    .replaceAll(/[;/\\]/g, "")
    .toLowerCase();
};
